import {
  FeatureToggleIdentifier,
  isFeatureEnabled,
} from "@/services/feature-toggle-adapter";
import { retrieveValue } from "../../store/persistence";

window.dataLayer = window.dataLayer || [];
window.adobeDataLayer = window.adobeDataLayer || [];

const deviceCheck = /Mobile/i.test(window.navigator.userAgent)
  ? "Mobile"
  : "Desktop";
const currentUser = retrieveValue("currentUser");

const DEFAULT_PAGE_VIEW_VALUES = {
  deviceType: deviceCheck,
  domainName: "unuhealth.org",
  loginStatus: currentUser ? "logged in" : "guest",
  siteBusinessUnit: "Group",
  siteCountry: "South Africa",
  websiteName: "Unu health",
  websiteNameCode: "UH",
  siteLanguage: "English",
  memberIdentifier: currentUser ? currentUser.memberId : "",
};

export interface EventPayload {
  event: string;
  [key: string]: any;
}

type DeviceType = "Mobile" | "Desktop";
export type AnalyticsLinkIntent =
  | "navigational"
  | "informational"
  | "comparison"
  | "transactional"
  | "confirmational"
  | "query"
  | "social";

export enum AnalyticsEvent {
  VIRTUAL_PAGE_VIEW = "virtualPageView",
  USER_INTERACTION = "userInteraction",
  FORM_START = "formStart",
  FORM_COMPLETE = "formComplete",
  APPLICATION_START = "applicationStart",
  APPLICATION_COMPLETE = "applicationComplete",
  ERROR = "errorEvent",
}

export enum AnalyticsErrorEvent {
  INTERNAL_SERVER_ERROR = "500 | internal server error",
  PAGE_NOT_FOUND = "404 | page not found",
  REQUEST_TIMED_OUT = "408 | request timed out",
  SERVICE_UNAVAILABLE = "503 | service unavailable",
}

interface AnalyticsPageViewEventPayload {
  pageName: string;
  pageSubSection1: string;
  pageSubSection2: string;
  pageCategory: string;
  domainName?: string;
  deviceType?: DeviceType;
  loginStatus?: string;
  memberIdentifier?: string;
  siteBusinessUnit?: string;
  siteCountry?: string;
  siteLanguage?: string;
  websiteName?: string;
  websiteNameCode?: string;
}

interface AnalyticsFormEventPayload {
  formName: string;
}

interface AnalyticsApplicationEventPayload {
  applicationName: string;
  applicationStep: string;
}

interface AnalyticsErrorEventPayload {
  errorMessage: string;
}

export interface AnalyticsUserInteractionEventPayload {
  linkText: string;
  linkIntent: AnalyticsLinkIntent;
  linkScope: string;
}

export function trackEvent(payload: EventPayload) {
  if (import.meta.env.VITE_APP_GOOGLE_TAG_MANAGER_URL_PARAMS) {
    window.dataLayer.push(payload);
  }
}

function pushAdobeEvent(payload: EventPayload) {
  if (!isFeatureEnabled(FeatureToggleIdentifier.ENABLE_ADOBE_ANALYTICS)) {
    return;
  }
  try {
    window.adobeDataLayer.push(payload);
  } catch {
    // A tag-property failure must not interrupt a member journey.
  }
}

export function trackPageViewV2(payload: AnalyticsPageViewEventPayload) {
  const pageViewEvent = { ...DEFAULT_PAGE_VIEW_VALUES, ...payload };
  pushAdobeEvent({
    event: AnalyticsEvent.VIRTUAL_PAGE_VIEW,
    page: pageViewEvent,
  });
}

export function trackUserInteraction(
  payload: AnalyticsUserInteractionEventPayload
) {
  pushAdobeEvent({
    event: AnalyticsEvent.USER_INTERACTION,
    cta: {
      ...payload,
      memberIdentifier: currentUser ? currentUser.memberId : "",
    },
  });
}

export function trackFormEvent(
  event: AnalyticsEvent.FORM_COMPLETE | AnalyticsEvent.FORM_START,
  payload: AnalyticsFormEventPayload
) {
  pushAdobeEvent({
    event: event,
    form: {
      ...payload,
      memberIdentifier: currentUser ? currentUser.memberId : "",
    },
  });
}

export function trackApplicationEvent(
  event: AnalyticsEvent.APPLICATION_START | AnalyticsEvent.APPLICATION_COMPLETE,
  payload: AnalyticsApplicationEventPayload
) {
  pushAdobeEvent({
    event: event,
    application: {
      ...payload,
      memberIdentifier: currentUser ? currentUser.memberId : "",
    },
  });
}

export function trackErrorEvent(payload: AnalyticsErrorEventPayload) {
  pushAdobeEvent({
    event: AnalyticsEvent.ERROR,
    errorMessage: {
      ...payload,
      memberIdentifier: currentUser ? currentUser.memberId : "",
    },
  });
}
